/**
 * @file CompanyPlanCard.tsx
 * @brief Company Admin → Billing: "Your plan" (Free or Business) with Get
 *        Business, Cancel and Resume; and the Get Business sheet.
 *
 * @details
 * Plans-visible brief B (21-10-2026). A company admin had no way to see or
 * buy Business. The card is the shared PlanSummaryCard; the numbers come from
 * planSummary.ts (the server's `companyPlan` once BACKEND Part 1 ships, else
 * today's `teamPlan` + catalogue).
 *
 * Get Business: seats = the company's active members, never below the
 * minimum (the server counts them the same way), price per seat, the total,
 * (no VAT words: not VAT registered, W36), the company's default card, then Confirm. 3DS on the first
 * payment runs through confirmWithBank (one bank check at a time), then the
 * server re-reads Stripe (sync); "active" shows only when the server says so.
 * Yearly shows only once the server offers it (BACKEND Part 1: today a
 * company admin's yearly request is refused).
 *
 * Changing monthly ↔ yearly for a company also waits for Part 1 (today
 * /subscriptions/change is personal only); its button appears with
 * `companyPlan`.
 *
 * @author DES
 * @date 21-10-2026
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheck, CreditCard, LoaderCircle, TriangleAlert } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { Button } from "@/components/ui/Button";
import {
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { SkeletonRows } from "@/components/ui/Skeleton";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import { confirmWithBank } from "@/shared/stripe/confirmWithBank";
import { cn } from "@/shared/theme/cn";
import { ComparePlansTable } from "@/shared/billing/ComparePlansTable";
import { PlanSummaryCard } from "@/shared/billing/PlanSummaryCard";
import { yearlySavingPct } from "@/shared/billing/planSummary";
import { useCompanySeatCount, usePlanSummary } from "@/shared/billing/usePlanSummary";
import { myBillingKeys, useMySubscription } from "@/features/my-billing/api";
import { billingErrorView, isFinalRefusal, notAvailableOf } from "@/features/my-billing/billingErrors";
import { formatDay, price } from "@/features/my-billing/components/format";
import {
  cancelCompanyPlan,
  changeCompanyPlan,
  previewCompanyPlanChange,
  mapPaymentMethods,
  resumeCompanyPlan,
  subscribeCompanyBusiness,
  syncCompanyPlan,
  useCompanyPaymentMethods,
} from "../api";

export function CompanyPlanCard() {
  const { plan, isLoading } = usePlanSummary();
  const status = useMySubscription();
  const qc = useQueryClient();
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyInterval, setBuyInterval] = useState<"month" | "year">("month");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState<"cancel" | "resume" | null>(null);
  const [changeOpen, setChangeOpen] = useState(false);

  if (isLoading || !plan) return <SkeletonRows rows={2} twoLine />;

  const refresh = () => qc.invalidateQueries({ queryKey: myBillingKeys.all });
  const business = status.data?.catalog.find((p) => p.code === "business") ?? null;
  const onBusiness = plan.planCode === "business" && plan.live;
  // B1 (22-10-2026): a blocked country (or plans paused) gets the server's
  // sentence instead of Get Business / Upgrade. A live Business keeps its own buttons.
  const notAvailable = onBusiness ? null : notAvailableOf(status.data);
  const prices = status.data?.companyPlan?.prices ?? null;
  // Yearly must be on (SUBSCRIPTION_ANNUAL_ENABLED): the catalogue sends no
  // yearly price while it is off, even though companyPlan.prices still lists one.
  const canChange =
    onBusiness &&
    !plan.pastDue &&
    business?.annualPriceMinor != null &&
    prices?.month != null &&
    prices?.year != null;

  const run = async (kind: "cancel" | "resume") => {
    setBusy(kind);
    try {
      if (kind === "cancel") await cancelCompanyPlan();
      else await resumeCompanyPlan();
      await refresh();
      toast({
        variant: "success",
        title: kind === "cancel" ? "Business will end at the end of this period" : "Business continues",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: kind === "cancel" ? "Couldn't cancel the plan" : "Couldn't keep the plan",
        description: err instanceof ApiClientError ? err.message : "Please try again.",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PlanSummaryCard
        plan={plan}
        cardsHref="/company/billing"
        freeLine={
          notAvailable
            ? `${notAvailable.message}${notAvailable.code === "SUBSCRIPTIONS_PAUSED" ? " Members pay as they go." : ""}`
            : business?.priceMinor != null
            ? `Members pay as they go. Business gives every member a seat and ${business.includedMinutes?.toLocaleString("en-GB") ?? "pooled"} Intelligence minutes each, shared by the team, for ${price(business.priceMinor, business.currency)} per seat a month.`
            : "Members pay as they go."
        }
        actions={
          plan.planCode === "enterprise" || notAvailable ? null : !onBusiness ? (
            <Button
              size="sm"
              onClick={() => {
                setBuyInterval("month");
                setBuyOpen(true);
              }}
              disabled={!business}
            >
              Get Business
            </Button>
          ) : plan.cancelAtPeriodEnd ? (
            <Button size="sm" onClick={() => void run("resume")} disabled={busy !== null}>
              {busy === "resume" ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Keep Business"}
            </Button>
          ) : (
            <>
              {/* Monthly <-> yearly (ADR-049): only where the server offers it. */}
              {canChange ? (
                <Button variant="outline" size="sm" onClick={() => setChangeOpen(true)} disabled={busy !== null}>
                  Change plan
                </Button>
              ) : null}
              <Button variant="ghost" size="sm" onClick={() => setCancelOpen(true)} disabled={busy !== null}>
                {busy === "cancel" ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Cancel plan"}
              </Button>
            </>
          )
        }
      />

      {/* Compare plans (DES 21-10-2026): Free, Business, Enterprise from the
          catalogue; Upgrade opens Get Business. */}
      {plan.planCode === "enterprise" || notAvailable ? null : (
        <ComparePlansTable
          audience="company_admin"
          currentCode={plan.live || plan.planCode === "free" ? plan.planCode : "free"}
          action={(row, iv) =>
            row.code === "business" && !onBusiness && business ? (
              <Button
                size="sm"
                className="w-full"
                onClick={() => {
                  setBuyInterval(iv);
                  setBuyOpen(true);
                }}
              >
                Upgrade
              </Button>
            ) : row.code === "free" && onBusiness ? (
              <p className="text-xs font-normal text-muted-foreground">Members pay as they go if Business ends.</p>
            ) : null
          }
        />
      )}

      <GetBusinessSheet open={buyOpen} onOpenChange={setBuyOpen} startInterval={buyInterval} />
      {canChange && plan.interval ? (
        <ChangePlanSheet
          open={changeOpen}
          onOpenChange={setChangeOpen}
          from={plan.interval}
          seats={plan.seats}
          prices={{ month: prices!.month!, year: prices!.year! }}
          currency={plan.currency}
          periodEnd={plan.periodEnd}
        />
      ) : null}

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Business?</AlertDialogTitle>
            <AlertDialogDescription>
              Business stays on until {formatDay(plan.periodEnd) ?? "the end of this period"}, then members pay as
              they go. You can keep it any time before then.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Business</AlertDialogCancel>
            <AlertDialogAction onClick={() => void run("cancel")}>Cancel at period end</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

type Phase =
  | { kind: "confirm" }
  | { kind: "working"; note: string }
  | { kind: "done" }
  | { kind: "error"; title: string; message: string; final?: boolean };

function GetBusinessSheet({
  open,
  onOpenChange,
  startInterval = "month",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The interval picked in Compare plans (21-10-2026). */
  startInterval?: "month" | "year";
}) {
  const { user } = useAuth();
  const companyId = user?.company_id ?? user?.companyId ?? null;
  const status = useMySubscription();
  const seatsQuery = useCompanySeatCount(open);
  const cardsQuery = useCompanyPaymentMethods(open ? companyId : null);
  const qc = useQueryClient();
  const [interval, setInterval] = useState<"month" | "year">(startInterval);
  const [phase, setPhase] = useState<Phase>({ kind: "confirm" });
  // One purchase at a time, even on a double click (the in-flight lesson).
  const inFlight = useRef(false);

  const business = status.data?.catalog.find((p) => p.code === "business") ?? null;
  const minSeats = business?.minSeats ?? 2;
  const members = seatsQuery.data ?? null;
  const seats = members != null ? Math.max(minSeats, members) : null;
  // Yearly for a company comes with BACKEND Part 1 (its `companyPlan` says so).
  const yearlyOffered = !!status.data?.companyPlan && business?.annualPriceMinor != null;
  useEffect(() => {
    if (open) setInterval(yearlyOffered ? startInterval : "month");
  }, [open, startInterval, yearlyOffered]);
  const perSeat = interval === "year" ? (business?.annualPriceMinor ?? null) : (business?.priceMinor ?? null);
  const total = perSeat != null && seats != null ? perSeat * seats : null;
  const saving = yearlySavingPct(business?.priceMinor, business?.annualPriceMinor);
  const cards = useMemo(() => mapPaymentMethods(cardsQuery.data), [cardsQuery.data]);
  const card = cards.find((c) => c.isDefault) ?? cards[0] ?? null;
  const currency = business?.currency ?? "GBP";
  const working = phase.kind === "working";

  const close = (v: boolean) => {
    if (working) return;
    onOpenChange(v);
    if (!v) setPhase({ kind: "confirm" });
  };

  const fail = (code: string | undefined, message: string) => {
    const view = billingErrorView(code, "company_admin", message);
    setPhase({ kind: "error", title: view?.title ?? "Payment not completed", message: view?.message ?? message, final: isFinalRefusal(code) });
  };

  const buy = async () => {
    if (inFlight.current || !card) return;
    inFlight.current = true;
    setPhase({ kind: "working", note: "Taking payment…" });
    try {
      const res = await subscribeCompanyBusiness({ interval, paymentMethodId: card.id });
      if (res.clientSecret) {
        setPhase({ kind: "working", note: "Waiting for your bank to confirm…" });
        const bank = await confirmWithBank(res.clientSecret, res.subscriptionId);
        if (!bank.ok) {
          fail(bank.code, bank.message);
          return;
        }
      }
      setPhase({ kind: "working", note: "Checking with Stripe…" });
      await syncCompanyPlan(res.subscriptionId);
      await qc.invalidateQueries({ queryKey: myBillingKeys.all });
      const fresh = qc.getQueryData<{ teamPlan?: { status?: string | null } | null; companyPlan?: { status?: string | null } | null }>(
        myBillingKeys.status(),
      );
      const st = fresh?.companyPlan?.status ?? fresh?.teamPlan?.status ?? null;
      if (st === "active" || st === "trialing") setPhase({ kind: "done" });
      else fail(undefined, "The payment hasn't gone through yet, so Business isn't active. Check the card, then try again.");
    } catch (err) {
      if (err instanceof ApiClientError) {
        fail(err.code, err.status === 409 ? "The company already has a plan." : err.message);
      } else {
        fail(undefined, err instanceof Error ? err.message : "Please try again.");
      }
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <FormSheet open={open} onOpenChange={close}>
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>{phase.kind === "done" ? "Business is active" : "Get Business"}</FormSheetTitle>
            <FormSheetDescription>
              {phase.kind === "done"
                ? "Stripe confirmed the payment. Every member has a seat."
                : "Check the seats, the amount and the card before you pay."}
            </FormSheetDescription>
          </FormSheetHeader>
          <FormSheetBody>
            {phase.kind === "done" ? (
              <FormSheetSection title="All set">
                <div className="flex items-start gap-3 px-4 py-4 md:px-5">
                  <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                  <p className="text-sm">The receipt is on the Invoices tab.</p>
                </div>
              </FormSheetSection>
            ) : (
              <>
                {yearlyOffered ? (
                  <FormSheetSection title="Billing">
                    <div className="flex flex-wrap items-center gap-3 px-4 py-4 md:px-5">
                      <div role="radiogroup" aria-label="Billing interval" className="inline-flex rounded-full bg-muted p-1 text-sm">
                        {(["month", "year"] as const).map((iv) => (
                          <button
                            key={iv}
                            type="button"
                            role="radio"
                            aria-checked={interval === iv}
                            disabled={working}
                            onClick={() => setInterval(iv)}
                            className={cn(
                              "rounded-full px-4 py-1.5 font-medium transition-colors",
                              interval === iv ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                            )}
                          >
                            {iv === "month" ? "Monthly" : "Yearly"}
                          </button>
                        ))}
                      </div>
                      {saving ? <span className="text-sm text-success">Yearly saves {saving}%</span> : null}
                    </div>
                  </FormSheetSection>
                ) : null}
                <FormSheetSection title="Seats">
                  <div className="px-4 py-4 text-sm md:px-5">
                    {seatsQuery.isLoading ? (
                      <LoaderCircle className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : seats != null ? (
                      <>
                        <p className="text-base font-semibold">{seats} seats</p>
                        <p className="mt-1 text-muted-foreground">
                          Every active member gets a seat, you included. Seats follow your team: adding or removing a
                          member changes the next bill.
                          {members != null && members < minSeats
                            ? ` You have ${members}, and Business starts at ${minSeats} seats, so you pay for ${minSeats}.`
                            : ""}
                        </p>
                      </>
                    ) : (
                      <p className="text-muted-foreground">Couldn't count your members. Please try again.</p>
                    )}
                  </div>
                </FormSheetSection>
                <FormSheetSection title="You pay">
                  <div className="px-4 py-4 md:px-5">
                    <p className="text-2xl font-semibold tracking-tight">
                      {total != null ? price(total, currency) : "—"}
                      <span className="ml-2 text-sm font-normal text-muted-foreground">today</span>
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {perSeat != null ? `${price(perSeat, currency)} per seat ${interval === "year" ? "a year" : "a month"}` : ""}
                      {seats != null ? ` × ${seats} seats. ` : ". "}
                      Then every {interval === "year" ? "year" : "month"} until you cancel.
                    </p>
                    {business?.includedMinutes ? (
                      <p className="mt-2 text-sm">
                        Includes {business.includedMinutes.toLocaleString("en-GB")} Intelligence minutes per seat{" "}
                        {interval === "year" ? "each month" : "a month"}, shared by the team.
                      </p>
                    ) : null}
                  </div>
                </FormSheetSection>
                <FormSheetSection title="Card">
                  <div className="flex items-center justify-between gap-3 px-4 py-4 md:px-5">
                    {cardsQuery.isLoading ? (
                      <LoaderCircle className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : card ? (
                      <p className="flex items-center gap-2 text-sm">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        {card.brand.charAt(0).toUpperCase() + card.brand.slice(1)} •••• {card.last4}
                        <button type="button" className="ml-2 text-xs text-muted-foreground underline" onClick={() => close(false)}>
                          Change
                        </button>
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">Add a company card below first, then come back here.</p>
                    )}
                  </div>
                </FormSheetSection>
                {phase.kind === "error" ? (
                  <div className="flex items-start gap-3 border-b border-r border-border/60 bg-destructive/5 px-4 py-4 md:px-5" role="alert">
                    <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                    <div className="text-sm">
                      <p className="font-semibold">{phase.title}</p>
                      <p className="mt-0.5 text-muted-foreground">{phase.message}</p>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </FormSheetBody>
          <FormSheetFooter>
            <FormSheetFooterCell>
              {phase.kind === "done" || (phase.kind === "error" && phase.final) ? (
                <Button
                  type="button"
                  onClick={() => close(false)}
                  className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
                >
                  {phase.kind === "done" ? "Done" : "Close"}
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={working || !card || total == null}
                  onClick={() => void buy()}
                  className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
                >
                  {working ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      {phase.note}
                    </>
                  ) : phase.kind === "error" ? (
                    "Try again"
                  ) : total != null ? (
                    `Pay ${price(total, currency)}`
                  ) : (
                    "Pay"
                  )}
                </Button>
              )}
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}

/**
 * Business monthly <-> yearly (ADR-049). The numbers come from the server's
 * /change/preview (Stripe's upcoming invoice: what is charged today, or the
 * credit left), shown before Confirm. 3DS on that charge runs through
 * confirmWithBank, then sync; the card refreshes from the server.
 */
function ChangePlanSheet({
  open,
  onOpenChange,
  from,
  seats,
  prices,
  currency,
  periodEnd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  from: "month" | "year";
  seats: number | null;
  prices: { month: number; year: number };
  currency: string;
  periodEnd: string | null;
}) {
  // The target is fixed when the sheet opens: after the change the plan
  // already reads the new interval, and the done screen must not flip.
  const [to, setTo] = useState<"month" | "year">(from === "month" ? "year" : "month");
  useEffect(() => {
    if (open) setTo(from === "month" ? "year" : "month");
    // Only on opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>({ kind: "confirm" });
  const inFlight = useRef(false);
  const preview = useQuery({
    queryKey: ["company-billing", "change-preview", to],
    enabled: open,
    retry: false,
    staleTime: 30_000,
    queryFn: () => previewCompanyPlanChange(to),
  });
  const working = phase.kind === "working";
  const perSeat = prices[to];
  const n = preview.data?.seats ?? seats;
  const saving = yearlySavingPct(prices.month, prices.year);
  const cur = preview.data?.currency ?? currency;

  const close = (v: boolean) => {
    if (working) return;
    onOpenChange(v);
    if (!v) setPhase({ kind: "confirm" });
  };

  const confirm = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPhase({ kind: "working", note: "Changing the plan…" });
    try {
      const res = await changeCompanyPlan(to);
      if (res.clientSecret) {
        setPhase({ kind: "working", note: "Waiting for your bank to confirm…" });
        const bank = await confirmWithBank(res.clientSecret, res.subscriptionId ?? "");
        if (!bank.ok) {
          const view = billingErrorView(bank.code, "company_admin", null);
          setPhase({ kind: "error", title: view?.title ?? "Plan not changed", message: view?.message ?? bank.message });
          return;
        }
        if (res.subscriptionId) await syncCompanyPlan(res.subscriptionId);
      }
      await qc.invalidateQueries({ queryKey: myBillingKeys.all });
      setPhase({ kind: "done" });
    } catch (err) {
      const code = err instanceof ApiClientError ? err.code : undefined;
      const view = billingErrorView(code, "company_admin", err instanceof ApiClientError ? err.message : null);
      setPhase({
        kind: "error",
        title: view?.title ?? "Plan not changed",
        message: view?.message ?? (err instanceof ApiClientError ? err.message : "Please try again."),
        final: isFinalRefusal(code),
      });
    } finally {
      inFlight.current = false;
    }
  };

  return (
    <FormSheet open={open} onOpenChange={close}>
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>
              {phase.kind === "done" ? "Plan changed" : to === "year" ? "Switch to yearly" : "Switch to monthly"}
            </FormSheetTitle>
            <FormSheetDescription>
              {phase.kind === "done"
                ? `Business is now billed ${to === "year" ? "yearly" : "monthly"}.`
                : "Check what changes and what is charged today before you confirm."}
            </FormSheetDescription>
          </FormSheetHeader>
          <FormSheetBody>
            {phase.kind === "done" ? (
              <FormSheetSection title="All set">
                <div className="flex items-start gap-3 px-4 py-4 md:px-5">
                  <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                  <p className="text-sm">The receipt for any charge is on the Invoices tab.</p>
                </div>
              </FormSheetSection>
            ) : (
              <>
                <FormSheetSection title="New price">
                  <div className="px-4 py-4 text-sm md:px-5">
                    <p className="text-base font-semibold">
                      {price(perSeat, cur)} per seat {to === "year" ? "a year" : "a month"}
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      {n != null ? `${n} seats: ${price(perSeat * n, cur)} every ${to === "year" ? "year" : "month"}. ` : ""}
                      {to === "year" && saving ? `About ${saving}% less than paying monthly. ` : ""}
                      {to === "month" ? `Yearly ends now; monthly starts today.` : ""}
                    </p>
                  </div>
                </FormSheetSection>
                <FormSheetSection title="Today">
                  <div className="px-4 py-4 md:px-5">
                    {preview.isLoading ? (
                      <LoaderCircle className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : preview.data ? (
                      <>
                        <p className="text-2xl font-semibold tracking-tight" data-testid="change-amount-due">
                          {price(preview.data.amountDueCents, cur)}
                          <span className="ml-2 text-sm font-normal text-muted-foreground">charged today</span>
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Stripe's figure: the new period, less what's left of this one
                          {periodEnd ? ` (paid to ${formatDay(periodEnd)})` : ""}.
                          {preview.data.creditCents
                            ? ` ${price(preview.data.creditCents, cur)} stays as credit for your next bills.`
                            : ""}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {/* The server's own reason (e.g. ANNUAL_NOT_AVAILABLE), else ours. */}
                        {preview.error instanceof ApiClientError && preview.error.message
                          ? `${preview.error.message} The plan stays as it is.`
                          : "Couldn't work out today's charge, so the change can't be confirmed yet. Please try again."}
                      </p>
                    )}
                  </div>
                </FormSheetSection>
                {phase.kind === "error" ? (
                  <div className="flex items-start gap-3 border-b border-r border-border/60 bg-destructive/5 px-4 py-4 md:px-5" role="alert">
                    <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                    <div className="text-sm">
                      <p className="font-semibold">{phase.title}</p>
                      <p className="mt-0.5 text-muted-foreground">{phase.message}</p>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </FormSheetBody>
          <FormSheetFooter>
            <FormSheetFooterCell>
              {phase.kind === "done" || (phase.kind === "error" && phase.final) ? (
                <Button
                  type="button"
                  onClick={() => close(false)}
                  className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
                >
                  {phase.kind === "done" ? "Done" : "Close"}
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={working || !preview.data}
                  onClick={() => void confirm()}
                  className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
                >
                  {working ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      {phase.note}
                    </>
                  ) : phase.kind === "error" ? (
                    "Try again"
                  ) : preview.data && preview.data.amountDueCents > 0 ? (
                    `Pay ${price(preview.data.amountDueCents, cur)} and switch`
                  ) : (
                    `Switch to ${to === "year" ? "yearly" : "monthly"}`
                  )}
                </Button>
              )}
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}

